import { useEffect, useRef, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Flag,
  FlagOff,
  MailOpen,
  Paperclip,
} from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/ui/Spinner";
import { ManagedFileLink } from "@/components/ui/ManagedFileLink";
import { formatDateTime } from "@/utils/formatDate";
import {
  useEopDrafts,
  useEopThread,
  useSetReadState,
  type EopAttachment,
  type EopMessageDetail,
} from "@/features/eop_mail/api/mailbox";
import { AssignBar } from "./AssignBar";
import { DraftCard } from "./DraftCard";
import { ReplyComposer } from "./ReplyComposer";
import { sanitizeMailHtml } from "./sanitize";

/**
 * Cała rozmowa w jednym panelu — sprawa u operatora to wątek, nie pojedynczy mail.
 *
 * Domyślnie rozwinięta jest tylko OSTATNIA wiadomość; starsze zwinięte do jednego
 * wiersza. Odwrotny układ (wszystko rozwinięte) przy piętnastu mailach z cytatami
 * daje ekran, przez który nikt nie przewinie, a interesuje wyłącznie ostatni ruch.
 */
export function ThreadPanel({ messageId }: { messageId: string }) {
  const { data: thread, isLoading } = useEopThread(messageId);
  const { data: drafts } = useEopDrafts();
  const setReadState = useSetReadState();
  const [open, setOpen] = useState<Set<string>>(new Set());

  const last = thread?.[thread.length - 1];

  useEffect(() => {
    if (last) setOpen(new Set([last.id]));
  }, [last?.id]);

  // Otwarcie wątku = przeczytane dla zespołu (i lustrzane do Exchange) — ale
  // WYŁĄCZNIE przy wyborze wątku. Znacznik ustawiamy przy KAŻDYM wyborze, nie
  // tylko gdy było coś nieprzeczytanego: bez tego wątek otwarty jako przeczytany
  // miał pusty znacznik i ręczne „Nieprzeczytane" natychmiast wracało na
  // przeczytane, bo efekt brał zmianę stanu za nowe otwarcie.
  const autoRead = useRef<string | null>(null);
  const anyUnread = (thread ?? []).some((m) => m.read_state === "unread");
  useEffect(() => {
    if (!last || autoRead.current === last.id) return;
    autoRead.current = last.id;
    if (anyUnread) setReadState.mutate({ id: last.id, state: "read" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [last?.id, anyUnread]);

  if (isLoading || !thread || !last) return <PageLoader />;

  const threadDrafts = (drafts ?? []).filter(
    (d) =>
      d.status !== "discarded" &&
      (d.conversation_id === last.conversation_id ||
        thread.some((m) => m.id === d.in_reply_to)),
  );

  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  return (
    <div className="max-w-4xl space-y-3">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <h2 className="text-[15px] font-semibold leading-snug text-[var(--color-text-primary)]">
          {last.subject || "(bez tematu)"}
        </h2>
        {thread.length > 1 && (
          <span className="font-mono text-[11px] text-[var(--color-text-muted)]">
            {thread.length} wiadomości
          </span>
        )}
        {(thread ?? []).some((m) => m.read_state === "action") ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              setReadState.mutate({ id: last.id, state: "read", force: true })
            }
          >
            <FlagOff size={12} className="mr-1" />
            Zdejmij „wymaga akcji”
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setReadState.mutate({ id: last.id, state: "action" })}
          >
            <Flag size={12} className="mr-1" />
            Wymaga akcji
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setReadState.mutate({ id: last.id, state: "unread" })}
        >
          <MailOpen size={12} className="mr-1" />
          Nieprzeczytane
        </Button>
        {last.web_link && (
          <a
            href={last.web_link}
            target="_blank"
            rel="noreferrer"
            className="ml-auto inline-flex items-center gap-1 text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
          >
            otwórz w Outlooku <ExternalLink size={11} />
          </a>
        )}
      </div>

      <AssignBar message={last} />

      <div className="card divide-y divide-[var(--color-border)] !p-0">
        {thread.map((message) => (
          <MessageRow
            key={message.id}
            message={message}
            expanded={open.has(message.id)}
            onToggle={() => toggle(message.id)}
          />
        ))}
      </div>

      <ReplyComposer message={last} onCreated={() => undefined} />

      {threadDrafts.map((draft) => (
        <DraftCard key={draft.id} draft={draft} />
      ))}
    </div>
  );
}

/**
 * Wklejone obrazki (logo z podpisu operatora) są częścią treści, nie dokumentami —
 * na liście załączników robią z jednego PDF-a rząd myślników. Inline'owe
 * NIE-obrazki zostają: bywają prawdziwymi dokumentami z dyspozycją inline.
 */
function documentAttachments(files: EopAttachment[]): EopAttachment[] {
  return files.filter(
    (file) => !(file.is_inline && file.content_type.startsWith("image/")),
  );
}

function MessageRow({
  message,
  expanded,
  onToggle,
}: {
  message: EopMessageDetail;
  expanded: boolean;
  onToggle: () => void;
}) {
  const outgoing = message.direction === "outgoing";
  const files = documentAttachments(message.attachments);
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-baseline gap-2 px-3 py-2 text-left hover:bg-[var(--color-bg-elevated)]"
      >
        {expanded ? (
          <ChevronDown size={13} className="shrink-0 translate-y-0.5" />
        ) : (
          <ChevronRight size={13} className="shrink-0 translate-y-0.5" />
        )}
        <span
          className={`shrink-0 text-[12px] font-medium ${
            outgoing
              ? "text-[var(--color-text-muted)]"
              : "text-[var(--color-text-primary)]"
          }`}
        >
          {outgoing ? "my" : message.from_name || message.from_address}
        </span>
        {!expanded && (
          <span className="truncate text-[11px] text-[var(--color-text-muted)]">
            {message.body_preview}
          </span>
        )}
        {message.attachment_count > 0 && (
          <span className="ml-auto inline-flex shrink-0 items-center gap-0.5 text-[11px] text-[var(--color-text-muted)]">
            <Paperclip size={11} />
            {message.attachment_count}
          </span>
        )}
        <span
          className={`shrink-0 font-mono text-[11px] text-[var(--color-text-muted)] ${
            message.attachment_count > 0 ? "" : "ml-auto"
          }`}
        >
          {formatDateTime(message.received_at)}
        </span>
      </button>

      {expanded && (
        <div className="space-y-2 px-3 pb-3">
          <div className="font-mono text-[11px] text-[var(--color-text-muted)]">
            <span>od {message.from_address}</span>
            <span className="mx-2">·</span>
            <span>do {message.to_addresses.join(", ") || "—"}</span>
            {message.cc_addresses.length > 0 && (
              <>
                <span className="mx-2">·</span>
                <span>dw {message.cc_addresses.join(", ")}</span>
              </>
            )}
          </div>

          {message.body_html ? (
            <div
              className="rich-text-content max-h-[30rem] overflow-y-auto py-1 text-[13.5px] leading-relaxed text-[var(--color-text-primary)]"
              dangerouslySetInnerHTML={{ __html: sanitizeMailHtml(message.body_html) }}
            />
          ) : (
            <p className="whitespace-pre-wrap text-[13px] text-[var(--color-text-secondary)]">
              {message.body_preview || "(pusta treść)"}
            </p>
          )}

          {files.length > 0 && (
            <ul className="flex flex-wrap gap-x-4 gap-y-1">
              {files.map((file) => (
                <li key={file.id}>
                  <AttachmentRow file={file} />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function AttachmentRow({ file }: { file: EopAttachment }) {
  return (
    <span className="inline-flex items-center gap-1 text-[12px]">
      <Paperclip size={11} className="shrink-0 text-[var(--color-text-muted)]" />
      {file.url ? (
        <ManagedFileLink
          file={{
            url: file.url,
            scan_status: file.scan_status || undefined,
            original_filename: file.name,
          }}
        >
          {file.name}
        </ManagedFileLink>
      ) : (
        <>
          {/* Nazwa MUSI być widoczna także bez URL-a (skan w toku, błąd pobrania,
              plik za duży) — sam komunikat bez nazwy wygląda jak brak załącznika. */}
          <span className="text-[var(--color-text-secondary)]">{file.name}</span>
          <ManagedFileLink
            file={{ url: null, scan_status: file.scan_status || undefined }}
            empty={null}
          />
        </>
      )}
      {file.size_bytes != null && (
        <span className="text-[11px] text-[var(--color-text-muted)]">
          {Math.max(1, Math.round(file.size_bytes / 1024))} kB
        </span>
      )}
      {file.download_error && (
        <Badge color="orange">{file.download_error}</Badge>
      )}
    </span>
  );
}
